
import { handleAxiosException } from '@/api'
import { Entity, ExternalUserInfo } from '@/api/auth/register'
import { NewPasswordRequest, userApi } from '@/api/user'
import ErrorAlert from '@/components/alert/ErrorAlert.vue'
import store from '@/store'
import { civilites } from '@/types'
import countries, { getCountryLabel } from '@/utils/countries'
import isEmailValid from '@/utils/email'
import { isRequiredGenerator, rulePassword, rulePhoneValidGenerator, ruleRequired } from '@/utils/rules'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { AxiosResponse } from 'axios'
import { cloneDeep, isEqual } from 'lodash'
import Vue from 'vue'
import { formatDateTime } from '@/utils/dates'

export default Vue.extend({
  components: { ErrorAlert, ConfirmDialog },
  data() {
    return {
      loading: true,
      isFormValid: true,
      infoForm: {} as ExternalUserInfo,
      copyInfoForm: {} as ExternalUserInfo,
      entities: [] as Entity[],
      countryList: countries,
      tel: {
        value: undefined as string|undefined,
        valid: false,
      },
      errorInfo: '',
      saving: false,
      hasChanged: false,
      successSnackbar: false,

      newPasswordForm: {
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      } as NewPasswordRequest,
      isPasswordFormValid: true,
      showPassword: false,
      showConfirm: false,
      savingNewPassword: false,
      errorNewPassword: '',
      successPasswordSnackbar: false,
      timeoutLogout: 5000,
      privateKeyDialog: {
        active: false,
        loading: false,
        newKey: '',
        error: '',
      },
      confirmNoTvaDialog: false,
      isFrf: false,
      entityToDelete: undefined as Entity | undefined,
      showDeletionDialog: false,
      isValidEntity: [] as boolean[],
    }
  },
  methods: {
    getCountryLabel,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    inputPhone(value: string, phone: any) {
      this.infoForm.phone = value.replaceAll(' ', '')
      this.tel.valid = phone.isValid
    },
    formatDateTime,
    isEmailValid,
    changeIsFrf(event: boolean) {
      if (!event) {
        this.infoForm.frfNumber = undefined
      }
    },
    confirmNoTva() {
      this.infoForm.vatNumber = undefined
    },
    cancelNoTva() {
      this.infoForm.withoutVat = false
      this.infoForm.vatNumber = this.copyInfoForm.vatNumber
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    checkNoTva(event: boolean) {
      // const target = event.target.control ?? event.target
      // const isChecked = target.checked
      if (event) {
        this.confirmNoTvaDialog = true
      }
    },
    capitalizeWords(value: string): string {
      if (!value) {
        return ''
      }

      return value
        .toLowerCase()
        .split(' ')
        .map(segment => segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join('-'))
        .join(' ')
    },
    formatName() {
      if (this.infoForm.type === 'MAN') {
        this.infoForm.lastName = this.infoForm.lastName.toUpperCase()
      } else if (this.infoForm.type === 'COMPANY') {
        this.infoForm.lastName = this.capitalizeCompanyName(this.infoForm.lastName)
      }
    },
    capitalizeCompanyName(value: string): string {
      if (!value) {
        return ''
      }

      const forbiddenWords = ['le', 'la', 'les', 'l\'', 'un', 'une', 'de', 'du', 'des', 'd\'', 'au', 'aux', 'en', 'et']

      return value
        .toLowerCase()
        .split(' ')
        .map((word, index) => {
          const isFirstWord = index === 0

          if (word.includes("'")) {
            const [beforeApostrophe, afterApostrophe] = word.split("'")
            if (forbiddenWords.includes(`${beforeApostrophe}'`)) {
              return `${beforeApostrophe}'${afterApostrophe.charAt(0).toUpperCase()}${afterApostrophe.slice(1)}`
            }
          }

          if (!isFirstWord && forbiddenWords.includes(word)) {
            return word
          }

          return word.charAt(0).toUpperCase() + word.slice(1)
        })
        .join(' ')
    },
    rulePhoneValidGenerator,
    isRequiredGenerator,
    async loadData() {
      this.loading = true
      try {
        this.copyInfoForm = await userApi.get()
        this.infoForm = cloneDeep(this.copyInfoForm)
        this.tel = {
          valid: true,
          value: this.infoForm.phone,
        }
        this.entities = await userApi.getEntities()
        this.isValidEntity = this.entities.map(() => true)
      } catch (err) {
        this.errorInfo = this.$t(handleAxiosException(err, (r: AxiosResponse) => r.data?.message ?? 'form.error.global_error')) as string
      }
      this.loading = false
    },
    async save() {
      if ((this.$refs.info as HTMLFormElement).validate()) {
        this.saving = true
        try {
          await userApi.updateInfo(this.infoForm)
          this.copyInfoForm = this.infoForm
          this.infoForm = cloneDeep(this.copyInfoForm) // pour activer le watch
          this.saving = false
          this.successSnackbar = true
        } catch (err) {
          this.saving = false
          this.errorInfo = this.$t(handleAxiosException(err, (r: AxiosResponse) => r.data?.message ?? 'form.error.global_error')) as string
        }
      }
    },
    async saveNewPassword() {
      if ((this.$refs.newPassword as HTMLFormElement).validate()) {
        try {
          this.errorNewPassword = ''
          this.savingNewPassword = true
          await userApi.updatePassword(this.newPasswordForm)
          this.newPasswordForm = { oldPassword: '', newPassword: '', confirmNewPassword: '' }
          this.successPasswordSnackbar = true
          setTimeout(() => {
            store.expireToken()
          }, this.timeoutLogout)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
          this.savingNewPassword = false
          this.errorNewPassword = this.$t(handleAxiosException(err, (r: AxiosResponse) => r.data?.message ?? 'form.error.global_error')) as string
        }
      }
    },
    copy(value: string) {
      navigator.clipboard.writeText(value)
    },
    async confirmRegeneratePrivateKey() {
      this.privateKeyDialog.loading = true
      this.privateKeyDialog.error = ''
      userApi.generatePrivateKey().then(response => {
        this.infoForm.lastChangeKeyTime = response.lastChangeKeyTime
        this.privateKeyDialog.newKey = response.privateKey
      }).catch(e => {
        this.privateKeyDialog.error = handleAxiosException(e)
      }).then(() => {
        this.privateKeyDialog.loading = false
      })
    },
    closeRegeneratePrivateKeyDialog() {
      this.privateKeyDialog.loading = false
      this.privateKeyDialog.error = ''
      this.privateKeyDialog.active = false
      setTimeout(() => {
        this.privateKeyDialog.newKey = ''
      }, 500)
    },
    capitalizeIBAN(value: string) {
      this.infoForm.iban = value.toUpperCase()
    },
    capitalizeBIC(value: string) {
      this.infoForm.bic = value.toUpperCase()
    },

    async saveEntity(entity: Entity, index: number) {
      if (
        (this.$refs[`entity-${index}`] as unknown[]).length
        && ((this.$refs[`entity-${index}`] as unknown[])[0] as HTMLFormElement).validate()
      ) {
        this.saving = true
        try {
          if (entity.id) {
            await userApi.updateEntity(entity)
          } else {
            await userApi.createEntity(entity)
          }
          this.successSnackbar = true
        } catch (err) {
          this.errorInfo = this.$t(handleAxiosException(err, (r: AxiosResponse) => r.data?.message ?? 'form.error.global_error')) as string
        }
        this.saving = false
        await this.loadData()
      }
    },
    requestDeletion(entity: Entity) {
      if (entity.id) {
        this.entityToDelete = entity
        this.showDeletionDialog = true
      } else {
        this.deleteEntity(entity)
      }
    },
    closeDeletionDialog() {
      this.showDeletionDialog = false
      this.entityToDelete = undefined
    },
    async deleteEntity(entity: Entity) {
      this.closeDeletionDialog()
      await userApi.deleteEntity(entity.id)
      await this.loadData()
    },
  },
  computed: {
    internalUserExist(): boolean {
      return !!this.infoForm?.internalUser
    },
    civilites() {
      return civilites.map(el => ({ value: el, text: (this.$t(`civilite.${el}`)) }))
    },
    rulePassword() {
      return [ruleRequired].concat(rulePassword)
    },
    confirmNewPasswordError(): string|undefined {
      if (this.newPasswordForm.newPassword === this.newPasswordForm.confirmNewPassword) {
        return undefined
      } else {
        return this.$t('form.confirm_new_password.required') as string
      }
    },
  },
  mounted() {
    this.loadData()
  },
  watch: {
    infoForm: {
      deep: true,
      handler(val) {
        this.hasChanged = !isEqual(val, this.copyInfoForm)
        if (val.frfNumber) {
          this.isFrf = true
        }
      },
    },
  },
})
